<template>
	<div style="width: 100%">
		<div
			class="agenda-card-wrapper d-flex justify-space-between align-center px-4 pb-4"
			v-bind:style="{ paddingTop: agendaEvent.sponsored_by ? '37px' : '16px' }"
		>
			<div>
				<ProfileBadge
					v-if="agendaEvent.sponsored_by"
					isPin
					:pin="{ icon: sponsoredStarIcon, text: sponsoredLabel }"
				/>
				<div
					class="agenda-card-description mb-2"
					v-bind:style="{ color: isAgendaPast ? '#AAA' : 'var(--accent-color)' }"
				>
					{{ agendaEvent.events_agenda_text }}
				</div>
				<div
					class="agenda-card-date"
					v-bind:style="{ color: isAgendaPast ? '#AAA' : 'var(--accent-color)' }"
				>
					{{
						formatDateRange(
							agendaEvent.events_agenda_from,
							agendaEvent.events_agenda_to,
							agendaEvent.agendaTimezone
						)
					}}
				</div>
				<div
					class="agenda-card-location mb-1"
					v-if="agendaEvent.session_location"
					v-bind:style="{ color: isAgendaPast ? '#AAA' : 'var(--accent-color)' }"
				>
					<Icon
						type="pin"
						:color="isAgendaPast ? '#aaa' : 'var(--button-secondary-background-color)'"
						:size="12"
						:pr="4"
					/>
					{{ agendaEvent.session_location }}
				</div>

				<div
					v-for="{ track_color, track_id, track_name } in agendaEvent.tracks"
					:key="track_id"
					class="agenda-card-tag mb-1"
					:style="`color: ${track_color}`"
				>
					<p class="mb-1">
						<v-icon :color="track_color" dark small class="mr-1">
							mdi-circle
						</v-icon>

						{{ track_name }}
					</p>
				</div>
			</div>
			<div>
				<v-btn
					@click.prevent="toggleMyAgenda"
					class="agenda-card-schedule-btn elevation-0"
					:loading="
						isMyAgendaLoading && myAgendaIdIsLoading === agendaEvent.events_agenda_id
					"
				>
					<Icon :type="scheduleIcon.icon" :color="scheduleIcon.color" :size="28" />
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { mapActions, mapGetters } from 'vuex'
import sponsoredStarIcon from '@/assets/images/icon-sponsored-star.svg'
import ProfileBadge from '@/components/ProfileBadge.vue'

Vue.use(VueScrollTo)

export default {
	name: 'AgendaCard',
	components: {
		ProfileBadge,
	},
	props: {
		agendaEvent: {
			type: Object,
			default: () => {},
		},
		selectedButton: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			sponsoredStarIcon,
		}
	},
	methods: {
		...mapActions('event', ['addMyAgenda', 'removeMyAgenda']),

		formatDateRange(dateFrom, dateTo, timezone) {
			const options = {
				weekday: 'long',
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: 'UTC',
			}

			//Convert Unix timestamps to JavaScript Date objects
			const fromDate = new Date(dateFrom * 1000)
			const toDate = new Date(dateTo * 1000)
			const timezoneOffset = Number(timezone.timezone_utc.slice(-2))
			const action = timezone.timezone_utc.slice(3, 4)
			if (action === '-') {
				fromDate.setHours(fromDate.getHours() - timezoneOffset)
				toDate.setHours(toDate.getHours() - timezoneOffset)
			} else {
				fromDate.setHours(fromDate.getHours() + timezoneOffset)
				toDate.setHours(toDate.getHours() + timezoneOffset)
			}

			// Format the dates
			const formattedFromDate = fromDate.toLocaleString('en-US', options)
			const formattedToDate = toDate.toLocaleTimeString('en-US', options)

			// Construct the formatted date range string
			return `${formattedFromDate} - ${formattedToDate}`
		},
		toggleMyAgenda() {
			if (!this.isMyAgendaLoading) {
				if (this.agendaEvent.is_scheduled) {
					this.removeMyAgenda({
						id: this.agendaEvent.events_agenda_id,
						myAgenda: this.$route.name === 'my-agenda-events',
						single: false,
						filter: this.currentFilter,
					})
				} else {
					this.addMyAgenda({ id: this.agendaEvent.events_agenda_id, single: false })
				}
			}
		},
	},
	computed: {
		...mapGetters('event', ['isMyAgendaLoading', 'myAgendaIdIsLoading']),
		...mapGetters('settings', ['styles']),

		sponsoredLabel() {
			return this.agendaEvent.sponsored_by
				? `Sponsored by ${this.agendaEvent.sponsored_by}`
				: ''
		},
		scheduleIcon() {
			return this.agendaEvent.is_scheduled
				? {
						icon: 'calendar_check',
						color: '#9B9B9B',
				  }
				: { icon: 'calendar_star', color: this.styles.button_main_background }
		},
		isAgendaPast() {
			const date = new Date()
			const currentGMTDate = date.getTime() + date.getTimezoneOffset() * 60 * 1000
			const agendaTimezoneOffset =
				+this.agendaEvent.agendaTimezone.timezone_utc.slice(-2) * 60 * 60 * 1000
			const agendaTimeZoneOffsetDirection =
				this.agendaEvent.agendaTimezone.timezone_utc.slice(3, 4) === '+'
			const agendaDate = new Date(this.agendaEvent.events_agenda_to * 1000).getTime()
			const agendaGMTDate =
				agendaDate +
				(agendaTimeZoneOffsetDirection === '+'
					? -agendaTimezoneOffset
					: +agendaTimezoneOffset)
			return currentGMTDate > agendaGMTDate
		},
		currentFilter() {
			const registeredFilter = this.selectedButton.some((button) => button === 'registered')
			const scheduledFilter = this.selectedButton.some((button) => button === 'scheduled')
			return (registeredFilter && scheduledFilter) || (!registeredFilter && !scheduledFilter)
				? 'schedregist'
				: registeredFilter
				? 'registered'
				: 'scheduled'
		},
	},
}
</script>

<style lang="scss">
.agenda-card {
	&-wrapper {
		background: rgb(251, 251, 251);
		border-radius: 12px;
		position: relative;
		margin-bottom: 8px;
	}

	&-date {
		color: var(--accent-color);
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: 600;
		line-height: 22px;
		margin-bottom: 4px;
	}

	&-description {
		color: var(--accent-color);
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: 700;
		line-height: 19.37px;
		margin-bottom: 20px;
	}

	&-tag {
		display: flex;
		align-items: center;
		font-size: 13px;
		font-family: Montserrat, sans-serif;
		font-weight: 700;
		line-height: 19.37px;

		p {
			word-break: break-all;
		}
	}

	&-location {
		display: flex;
		color: var(--button-secondary-background-color);
		font-size: 13px;
		font-family: Montserrat, sans-serif;
		font-weight: 500;
		line-height: 19.37px;
		margin-bottom: 4px;
	}

	&-schedule {
		&-btn {
			background-color: transparent !important;
			padding: 0 8px !important;
			min-width: auto !important;
		}
	}
}
</style>
